import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Truck, Users, Award, MessageSquare, Clock, Package, Globe, CreditCard, Eye, CheckCircle, Star } from 'lucide-react';
import ProductCard from '../components/ProductCard'; 
import { getFeaturedProducts, categories } from '../data/products';
import CategoryCard3D from '../components/CategoryCard3D';
import StockLotSection from '../components/StockLot/StockLotSection';
import BlogSection from '../components/Blog/BlogSection';
import VideoFeed from '../components/VideoFeed/VideoFeed';

const HomePage = () => {
  const featuredProducts = getFeaturedProducts(4);

  const categoryButtons = [
    {
      title: 'Garments & Apparel',
      productCount: 125,
      slug: 'garments-apparel',
      color: 'rgb(59, 130, 246)' // Blue
    },
    {
      title: 'Jute & Natural Fiber',
      productCount: 95,
      slug: 'jute-natural-fiber',
      color: 'rgb(16, 185, 129)' // Green
    },
    {
      title: 'Frozen Foods',
      productCount: 75,
      slug: 'frozen-foods',
      color: 'rgb(6, 182, 212)' // Cyan
    },
    {
      title: 'Leather Goods',
      productCount: 85,
      slug: 'leather-goods',
      color: 'rgb(217, 119, 6)' // Amber
    },
    {
      title: 'Pharmaceuticals',
      productCount: 65,
      slug: 'pharmaceuticals-health',
      color: 'rgb(220, 38, 38)' // Red
    },
    {
      title: 'Scrap & Recycling',
      productCount: 55,
      slug: 'scrap-recycling',
      color: 'rgb(132, 204, 22)' // Lime
    },
    {
      title: 'Furniture',
      productCount: 78,
      slug: 'furniture',
      color: 'rgb(245, 158, 11)' // Amber
    },
    {
      title: 'Manpower',
      productCount: 62,
      slug: 'manpower',
      color: 'rgb(147, 51, 234)' // Purple
    },
    {
      title: 'Tourism',
      productCount: 45,
      slug: 'tourism',
      color: 'rgb(20, 184, 166)' // Teal
    },
    {
      title: 'Freelancer',
      productCount: 92,
      slug: 'freelancer',
      color: 'rgb(79, 70, 229)' // Indigo
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-madeinbd-gradient text-white py-20">
        <div className="absolute inset-0 bg-black/20" style={{ backgroundImage: `url('/openart-d8725499-52ed-4daa-84e0-9a6aa935f590 copy.jpeg')`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.8, mixBlendMode: 'overlay' }}></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Discover 
                <span className="text-yellow-300"> Authentic</span>
                <br />Products from Bangladesh
              </h1>
              <p className="text-xl mb-8 text-orange-100">
                Connect directly with Bangladeshi manufacturers and artisans. 
                From traditional handicrafts to modern innovations, explore the best of Bangladesh.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link 
                  to="/products"
                  className="bg-white text-madeinbd-700 px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-full font-semibold hover:bg-gray-100 transition-all duration-200 flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105 w-full sm:w-auto"
                >
                  <span>Explore Products</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <Link 
                  to="/rfq/create"
                  className="bg-blue-600 text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105 w-full sm:w-auto"
                >
                  <span>Submit RFQ</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-madeinbd-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-madeinbd-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Verified Sellers</h3>
              <p className="text-gray-600">All sellers are verified and authenticated</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Global Shipping</h3>
              <p className="text-gray-600">Worldwide delivery with tracking</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Direct Connection</h3>
              <p className="text-gray-600">Connect directly with manufacturers</p>
            </div>
            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Quality Assured</h3>
              <p className="text-gray-600">Premium quality products guaranteed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Explore Our Product Categories
            </h2>
            <p className="text-xl text-gray-600">
              Discover authentic Bangladeshi products across various industries
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Garments & Apparel */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
              <div className="relative h-48 bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <img 
                  src="https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg" 
                  alt="Garments & Apparel"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity duration-300"
                />
                <div className="absolute inset-0 bg-blue-600/20"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Garments & Apparel</h3>
                <p className="text-gray-600 mb-4">
                  Premium quality clothing including t-shirts, polo shirts, casual wear, and traditional garments made from finest Bangladeshi cotton.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">125+ Products</span>
                  <Link 
                    to="/category/garments-apparel"
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center space-x-1"
                  >
                    <span>Explore</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Jute & Natural Fiber */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
              <div className="relative h-48 bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <img 
                  src="https://images.pexels.com/photos/5217959/pexels-photo-5217959.jpeg" 
                  alt="Jute & Natural Fiber"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity duration-300"
                />
                <div className="absolute inset-0 bg-green-600/20"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Jute & Natural Fiber</h3>
                <p className="text-gray-600 mb-4">
                  Eco-friendly jute products including bags, rugs, handicrafts, and home decor items crafted by skilled artisans using traditional techniques.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">95+ Products</span>
                  <Link 
                    to="/category/jute-natural-fiber"
                    className="text-green-600 hover:text-green-700 font-medium text-sm flex items-center space-x-1"
                  >
                    <span>Explore</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Frozen Foods */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
              <div className="relative h-48 bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <img 
                  src="https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg" 
                  alt="Frozen Foods"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity duration-300"
                />
                <div className="absolute inset-0 bg-cyan-600/20"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Frozen Foods</h3>
                <p className="text-gray-600 mb-4">
                  Fresh frozen seafood including hilsa fish, prawns, and other premium aquatic products sourced from Bangladesh's pristine waters.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">75+ Products</span>
                  <Link 
                    to="/category/frozen-foods"
                    className="text-cyan-600 hover:text-cyan-700 font-medium text-sm flex items-center space-x-1"
                  >
                    <span>Explore</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          
          {/* See All Products Button */}
          <div className="text-center">
            <Link 
              to="/products"
              className="inline-flex items-center space-x-2 bg-madeinbd-gradient text-white px-8 py-4 rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-lg"
            >
              <span>See All Products</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Source by Industry
            </h2>
            <p className="text-xl text-gray-600">
              Discover the diverse range of products Bangladesh has to offer
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6 justify-items-center bg-white p-4 sm:p-6 rounded-xl shadow-sm">
            {categoryButtons.map((category) => (
              <CategoryCard3D 
                key={category.slug} 
                title={category.title} 
                productCount={category.productCount} 
                slug={category.slug} 
                color={category.color}
              />
            ))}
          </div>
          
          {/* See All Products Button */}
          <div className="text-center mt-8">
            <Link 
              to="/products"
              className="inline-flex items-center space-x-2 bg-madeinbd-gradient text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity shadow-lg"
            >
              <span>See All Products</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Featured Products
            </h2>
            <p className="text-xl text-gray-600">
              Handpicked products from our best sellers
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Browse our featured products with multiple variants and options
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/products"
              className="inline-flex items-center space-x-2 bg-madeinbd-gradient text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
            >
              <span>View All Products</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works for International Buyers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Start sourcing from Bangladesh in 3 simple steps. Connect with verified suppliers and secure your deals with confidence.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {/* Step 1: Browse Suppliers */}
            <div className="text-center group">
              <div className="relative mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <Users className="h-10 w-10 md:h-12 md:w-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                  1
                </div>
                {/* Connecting line to next step (hidden on mobile) */}
                <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-blue-300 to-green-300 transform translate-x-4 lg:translate-x-8"></div>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Browse Verified Suppliers</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Explore our network of 500+ verified Bangladeshi suppliers. Filter by product category, location, and certifications to find the perfect match for your needs.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-center space-x-4 text-sm text-blue-700">
                  <div className="flex items-center">
                    <Shield className="h-4 w-4 mr-1" />
                    <span>Verified</span>
                  </div>
                  <div className="flex items-center">
                    <Award className="h-4 w-4 mr-1" />
                    <span>Certified</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-4 w-4 mr-1" />
                    <span>Rated</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Send Inquiry */}
            <div className="text-center group">
              <div className="relative mb-6">
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <MessageSquare className="h-10 w-10 md:h-12 md:w-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                  2
                </div>
                {/* Connecting line to next step (hidden on mobile) */}
                <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-green-300 to-orange-300 transform translate-x-4 lg:translate-x-8"></div>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Send Inquiry & Get Quotes</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Contact suppliers directly through our platform. Send detailed inquiries, request samples, and receive competitive quotes. Our AI assistant helps 24/7.
              </p>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center justify-center space-x-4 text-sm text-green-700">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>Fast Response</span>
                  </div>
                  <div className="flex items-center">
                    <Package className="h-4 w-4 mr-1" />
                    <span>Free Samples</span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="h-4 w-4 mr-1" />
                    <span>Global Shipping</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: Secure Deal */}
            <div className="text-center group">
              <div className="relative mb-6">
                <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <Shield className="h-10 w-10 md:h-12 md:w-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                  3
                </div>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Secure Deal with Escrow</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Complete your purchase with confidence using our secure escrow service. Your payment is protected until you confirm receipt and satisfaction with your order.
              </p>
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-center space-x-4 text-sm text-orange-700">
                  <div className="flex items-center">
                    <CreditCard className="h-4 w-4 mr-1" />
                    <span>Secure Payment</span>
                  </div>
                  <div className="flex items-center">
                    <Eye className="h-4 w-4 mr-1" />
                    <span>Quality Check</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    <span>Guaranteed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-blue-50 to-orange-50 p-6 md:p-8 rounded-2xl">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Ready to Start Sourcing?</h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Join thousands of international buyers who trust Made in BD for their sourcing needs. Start your journey today!
              </p>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-center">
                <Link 
                  to="/suppliers"
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 shadow-md"
                >
                  <Users className="h-5 w-5" />
                  <span>Browse Suppliers</span>
                </Link>
                <Link 
                  to="/rfq/create"
                  className="bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2 shadow-md"
                >
                  <MessageSquare className="h-5 w-5" />
                  <span>Send RFQ</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stock Lot Section */}
      <StockLotSection />

      {/* Video Feed Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <VideoFeed />
        </div>
      </section>

      {/* Blog Section */}
      <BlogSection />

      {/* Statistics Section */}
      <section className="py-16 bg-madeinbd-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-nowrap overflow-x-auto md:grid md:grid-cols-4 gap-8 text-center">
            <div className="flex-shrink-0 min-w-[100px] md:min-w-0">
              <div className="text-4xl font-bold mb-2">500+</div>
              <div className="text-orange-100">Products Listed</div>
            </div>
            <div className="flex-shrink-0 min-w-[100px] md:min-w-0">
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-orange-100">Verified Sellers</div>
            </div>
            <div className="flex-shrink-0 min-w-[100px] md:min-w-0">
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-orange-100">Countries Served</div>
            </div>
            <div className="flex-shrink-0 min-w-[100px] md:min-w-0">
              <div className="text-4xl font-bold mb-2">98%</div>
              <div className="text-orange-100">Customer Satisfaction</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;